import { useCallback } from "react";
import { getAssetType } from "domains/assets/utils/getIsType";
import { capitalizeFirstWord } from "domains/commons/misc";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import {
  GetAssetsByAssetIdApiResponse,
  useDeleteAssetMutation,
} from "infra/api/generated/api";

interface UseAssetDeleteProps {
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  trackingEvent: string;
}

export function useAssetDelete({ asset, trackingEvent }: UseAssetDeleteProps) {
  const { successToast } = useScenarioToast();
  const { selectedProject, isReader } = useTeamContext();
  const handleApiError = useHandleApiError();
  const [deleteImageTrigger, { isLoading: isDeleting }] =
    useDeleteAssetMutation();

  const canDelete = !isReader && asset?.ownerId === selectedProject.id;

  const handleDelete = useCallback(async () => {
    if (!asset) return;

    const assetType = getAssetType(asset).singular;

    try {
      await deleteImageTrigger({
        projectId: selectedProject.id,
        body: {
          assetIds: [asset.id],
        },
      });
      successToast({
        title: `${capitalizeFirstWord(assetType)} deleted`,
      });
      Track(trackingEvent, {
        assetId: asset.id,
      });
    } catch (error) {
      handleApiError(error, `Error deleting ${assetType}`);
    }
  }, [
    asset,
    deleteImageTrigger,
    selectedProject.id,
    successToast,
    handleApiError,
    trackingEvent,
  ]);

  return {
    handleDelete,
    isDeleting,
    canDelete,
  };
}
